/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable, OnDestroy} from '@angular/core';
import {merge, Observable, Subject} from 'rxjs';
import {ActionType, Picture} from 'compob2b-libs';
import {exhaustMap, filter, map, publishReplay, refCount, scan, startWith, tap} from 'rxjs/operators';
import {ExecutorAutocompleteService} from './executor-autocomplete.service';

let defaultState: AssigneeAutocompleteState = {
  items: [],
  loading: false
};
export interface IssueExecutor {
  id: number;
  firstName: string;
  lastName: string;
  picture?: Picture | null;
}
export interface Executor {
  id: number;
  header: string;
  picture: string;
}

export interface AssigneeAutocompleteState {
  items: Executor[];
  loading: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ExecutorAutocompleteStore implements OnDestroy {

  actions$: Subject<ActionType> = new Subject<ActionType>();

  load$: Observable<ActionType> = this.actions$.pipe(
    filter(_ => 'load' === _.type),
    exhaustMap(_ => this.service.loadExecutors()),
    map(payload => ({
      type: 'load success', payload
    })),
    tap(_ => defaultState = {items: _.payload, loading: false})
  );

  filter$: Observable<ActionType> =
    this.actions$.pipe(
      filter(_ => 'filter' === _.type),
      map((_: ActionType) => this.filterItems(_.payload)),
      map(payload => ({
        type: 'filter success', payload
      }))
    );

  dispatcher$: Observable<ActionType> =
    merge(this.actions$, this.load$, this.filter$);

  state$: Observable<AssigneeAutocompleteState|ActionType> = this.dispatcher$.pipe(
    //@ts-ignore
    startWith(defaultState),
    //@ts-ignore
    scan((state: AssigneeAutocompleteState, action: ActionType): AssigneeAutocompleteState => {
      switch (action.type) {
        case 'load':
        case 'filter':
          return { ...state, loading: true };
        case 'load success':
        case 'filter success':
          return { ...state, items: action.payload, loading: false };
        case 'reset':
          return defaultState;
        default:
          return state;
      }
    }),
    publishReplay(1),
    refCount()
  );

  items$: Observable<Executor[]> = this.state$.pipe(
      //@ts-ignore
      map((state: AssigneeAutocompleteState) => {
        if (!state.items) return [];

        return [...state.items].sort((a, b) => {
          const getSecondWord = (text: string): string => {
            if (!text) return '';
            const words = text.trim().split(/\s+/);
            return words.length > 1 ? words[1].toLowerCase() : words[0].toLowerCase();
          };

          const secondWordA = getSecondWord(a.header || '');
          const secondWordB = getSecondWord(b.header || '');

          return secondWordA.localeCompare(secondWordB);
        });
      }),
  );
  count$: Observable<number> =
    this.items$.pipe(map(_ => _.length));
  loading$: Observable<boolean> =
    //@ts-ignore
    this.state$.pipe(map((_:AssigneeAutocompleteState) => _.loading));

  constructor(private service: ExecutorAutocompleteService) {}

  ngOnDestroy(): void {
    this.actions$.complete();
  }

  load() { this.dispatch({type: 'load'}); }

  filter(keyword: string) { this.dispatch({type: 'filter', payload: keyword}); }

  reset() { this.dispatch({type: 'reset'}); }

  private dispatch(action: ActionType): void {
    this.actions$.next(action);
  }

  private filterItems(keyword: string): Executor[] {
    return defaultState.items.filter(item =>  item.header.toLocaleLowerCase().indexOf((keyword || '').toLocaleLowerCase()) !== -1);
  }
}
